import { Fragment, ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Login } from './Login';
import { SystemStatusHeader, type SystemStatusCounts } from './SystemStatusHeader';
import { useHealth } from '../hooks/useHealth';
import { useAuth } from '../hooks/useAuth';
import { isAdsEnabled, isInteriorEnabled, isVideoEnabled } from '../utils/api';
import { GenerateScanModal } from '../video/components/GenerateScanModal';
import { useVideoHealth } from '../video/hooks/useVideoHealth';
import { useInteriorHealth } from '../interior/hooks/useInteriorHealth';
import { useAdsHealth } from '../ads/hooks/useAdsHealth';
import { useApigenHealth } from '../apigen/hooks/useApigenHealth';
import { groupVersionsByMinor } from '../release-notes/groupVersions';
import { versions as releaseNotesVersions } from 'virtual:release-notes';

/**
 * Window event dispatched when a video scan is submitted from the
 * AppShell-level modal. The video Live Scans page listens for this and
 * forces an immediate refresh so the user sees their freshly-submitted
 * job without waiting up to 3s for the next poll tick.
 */
export const VIDEO_SCAN_SUBMITTED_EVENT = 'video:scan-submitted';

const GITHUB_REPO_URL = 'https://github.com/UVeye/360_lightning';

type AppMode = 'lightning' | 'video' | 'interior' | 'ads' | 'api' | 'cost' | 'release-notes';

/**
 * Inline icon used for the API-wrapper section, both as a sidebar/nav glyph
 * (rendered as an <svg> inheriting the text color) and, for the mobile app
 * switcher, as a mask image via `API_ICON_SRC`.
 */
const apiSectionIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 9l-3 3 3 3m8-6l3 3-3 3m-2-9l-4 12" />
  </svg>
);

const API_ICON_SRC =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='black' stroke-width='1.75' viewBox='0 0 24 24'><path stroke-linecap='round' stroke-linejoin='round' d='M8 9l-3 3 3 3m8-6l3 3-3 3m-2-9l-4 12'/></svg>",
  );

/**
 * Currency-dollar glyph for the Cost section, used both as a rail SVG
 * (inheriting the text color) and, for the mobile app switcher, as a
 * mask image via `COST_ICON_SRC`.
 */
const COST_ICON_SRC =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='black' stroke-width='1.75' viewBox='0 0 24 24'><path stroke-linecap='round' stroke-linejoin='round' d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'/></svg>",
  );

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * When set, the item renders grayed-out and non-clickable (used to gate
   * the interior "Manual Review" tab in `automatic` callback mode). The
   * string is surfaced as the element's `title` tooltip.
   */
  disabledReason?: string;
  /** Optional release-line heading shown above nested patch-version links. */
  groupLabel?: string;
  /** Stable identifier shared by every patch link in a collapsible group. */
  groupId?: string;
  /** Indent this link beneath its release-line heading. */
  nested?: boolean;
}

function NavGroupButton({
  label,
  expanded,
  onToggle,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex w-full items-center justify-between rounded-md px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600"
    >
      <span>{label}</span>
      <svg
        className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

const lightningNavItems: NavItem[] = [
  {
    to: '/lightning/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/lightning/live',
    label: 'Live Scans',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/lightning/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/lightning/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/lightning/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const interiorNavItems: NavItem[] = [
  {
    to: '/interior/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/interior/live',
    label: 'Live Scans',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/interior/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/interior/manual-verification',
    label: 'Manual Review',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/interior/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/interior/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const adsNavItems: NavItem[] = [
  {
    to: '/ads/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/ads/live',
    label: 'Live Jobs',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/ads/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/ads/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/ads/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const videoNavItems: NavItem[] = [
  {
    to: '/video/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/video/live',
    label: 'Live Scans',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/video/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/video/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/video/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const apiNavItems: NavItem[] = [
  {
    to: '/gen-api',
    label: 'Endpoints',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 10h16M4 14h10M4 18h10" />
      </svg>
    ),
  },
  { to: '/gen-api/erase', label: 'Erase', icon: apiSectionIcon },
  { to: '/gen-api/nano-banana-2', label: 'Nano Banana 2', icon: apiSectionIcon },
  { to: '/gen-api/nano-banana-lite', label: 'Nano Banana Lite', icon: apiSectionIcon },
  { to: '/gen-api/gpt-image-2', label: 'GPT Image 2', icon: apiSectionIcon },
  { to: '/gen-api/nano-banana-masked', label: 'Masked NB', icon: apiSectionIcon },
  { to: '/gen-api/wheel-replacement', label: 'Wheel Replacement', icon: apiSectionIcon },
  { to: '/gen-api/region-op', label: 'Blur / Erase Region', icon: apiSectionIcon },
  {
    to: '/gen-api/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/gen-api/stress',
    label: 'Stress Test',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

const costNavItems: NavItem[] = [
  {
    to: '/cost/services',
    label: 'All Services',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 8h8M8 12h8M8 16h5" />
      </svg>
    ),
  },
  {
    to: '/cost/merch',
    label: 'Merch Cost',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

function detectMode(pathname: string): AppMode {
  // Anything under `/video/*`, `/interior/*` or `/ads/*` puts the shell
  // into that sub-app's mode; everything else (including legacy paths
  // still in flight before the redirect settles) shows the lightning
  // navigation.
  if (pathname.startsWith('/video')) return 'video';
  if (pathname.startsWith('/interior')) return 'interior';
  if (pathname.startsWith('/ads')) return 'ads';
  if (pathname.startsWith('/gen-api')) return 'api';
  if (pathname.startsWith('/cost')) return 'cost';
  if (pathname.startsWith('/release-notes')) return 'release-notes';
  return 'lightning';
}

function appTitleFor(mode: AppMode): string {
  return mode === 'video'
    ? '360 Video'
    : mode === 'interior'
      ? '360 Interior'
      : mode === 'ads'
        ? '360 Ads'
        : mode === 'api'
          ? 'API'
          : mode === 'cost'
            ? 'Cost'
            : mode === 'release-notes'
              ? 'Release Notes'
              : '360 Lightning';
}

interface ModeRailProps {
  mode: AppMode;
  videoVisible: boolean;
  interiorVisible: boolean;
  adsVisible: boolean;
  homeLink: string;
}

interface ModeRailItem {
  to: string;
  label: string;
  modeId: AppMode;
  icon: ReactNode;
  iconSrc: string;
}

function AppLogo({ src, alt, className, colorClass }: { src: string, alt: string, className?: string, colorClass?: string }) {
  if (!colorClass) {
    return <img src={src} alt={alt} className={className} />;
  }
  return (
    <div
      className={`${className} ${colorClass}`}
      style={{
        WebkitMaskImage: `url(${src})`,
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskImage: `url(${src})`,
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
      }}
      role="img"
      aria-label={alt}
    />
  );
}

/**
 * Release Notes sub-bar: patch releases are nested under their major.minor
 * line while preserving the newest-first order baked into CHANGELOG.md.
 */
const releaseNotesNavItems: NavItem[] = groupVersionsByMinor(
  releaseNotesVersions,
).flatMap((group) =>
  group.entries.map((entry, index) => ({
    to: `/release-notes/${entry.version}`,
    label: `v${entry.version}`,
    groupLabel: group.nested && index === 0 ? `v${group.minorVersion}` : undefined,
    groupId: group.nested ? group.minorVersion : undefined,
    nested: group.nested,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 015 12V7a4 4 0 014-4z"
        />
      </svg>
    ),
  })),
);

/** Latest version = first changelog entry; used for the section landing route. */
const latestReleaseVersion = releaseNotesVersions[0]?.version ?? '';

// Document/notes icon used for the Release Notes mode-rail entry. The data
// URI variant feeds the mobile app-switcher's masked <AppLogo>.
const RELEASE_NOTES_ICON_SRC =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="%23000" stroke-width="1.75"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
  );

const modeRailItems: ModeRailItem[] = [
  {
    to: '/lightning/overview',
    label: 'Merch',
    modeId: 'lightning',
    iconSrc: '/lightning.png',
    icon: (
      <img src="/lightning.png" alt="Merch" className="w-12 h-12 object-contain" />
    ),
  },
  {
    to: '/video/overview',
    label: '360',
    modeId: 'video',
    iconSrc: '/video_360.png',
    icon: (
      <img src="/video_360.png" alt="360" className="w-12 h-12 object-contain" />
    ),
  },
  {
    to: '/interior/overview',
    label: 'Interior',
    modeId: 'interior',
    iconSrc: '/interior.png',
    icon: (
      <img src="/interior.png" alt="Interior" className="w-12 h-12 object-contain" />
    ),
  },
  {
    to: '/ads/overview',
    label: 'Ads',
    modeId: 'ads',
    iconSrc: '/ads.png',
    icon: (
      <img src="/ads.png" alt="Ads" className="w-12 h-12 object-contain" />
    ),
  },
  {
    to: '/gen-api',
    label: 'API',
    modeId: 'api',
    iconSrc: API_ICON_SRC,
    icon: (
      <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 9l-3 3 3 3m8-6l3 3-3 3m-2-9l-4 12" />
      </svg>
    ),
  },
  {
    to: '/cost/services',
    label: 'Cost',
    modeId: 'cost',
    iconSrc: COST_ICON_SRC,
    icon: (
      <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/release-notes',
    label: 'Release Notes',
    modeId: 'release-notes',
    iconSrc: RELEASE_NOTES_ICON_SRC,
    icon: (
      <svg
        className="w-9 h-9"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
];

function ModeRail({ mode, videoVisible, interiorVisible, adsVisible, homeLink }: ModeRailProps) {
  // Far-left rail that switches the whole app between the Lightning,
  // 360 Video and 360 Interior mini-apps. The `videoVisible` /
  // `interiorVisible` flags are only false in dev-mode builds where
  // the developer didn't set `VITE_VIDEO_API_BASE` /
  // `VITE_INTERIOR_API_BASE` in their `.env`; in that case we hide
  // the corresponding entry so the SPA doesn't surface a link that
  // would 502.
  const items = visibleModeRailItems(videoVisible, interiorVisible, adsVisible);
  const appTitle = appTitleFor(mode);
  // Release Notes is pinned to the very bottom of the rail; every other
  // mode stays in the top group.
  const topItems = items.filter((item) => item.modeId !== 'release-notes');
  const bottomItems = items.filter((item) => item.modeId === 'release-notes');

  const renderRailLink = (item: ModeRailItem) => {
    const isActive = item.modeId === mode;
    return (
      <Link
        to={item.to}
        role="tab"
        aria-selected={isActive}
        className={`group relative flex flex-col items-center justify-center gap-1 w-16 py-2 rounded-lg text-[10px] font-medium text-center leading-tight transition-all duration-200 ${
          isActive
            ? 'bg-white/15 text-white'
            : 'text-indigo-100 hover:bg-white/10 hover:text-white'
        }`}
      >
        {isActive && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-white to-indigo-200" />
        )}
        <span className={isActive ? '' : 'opacity-70 group-hover:opacity-100 transition-opacity'}>
          {item.icon}
        </span>
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <aside
      className="hidden md:flex w-20 shrink-0 flex-col items-center py-4 border-r border-indigo-600 bg-indigo-500 sticky top-0 h-screen self-start z-30"
      aria-label="App mode"
    >
      <Link
        to={homeLink}
        className="w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/30 flex items-center justify-center shadow-sm hover:bg-white/25 hover:shadow-md transition-all duration-200"
        title={appTitle}
      >
        <img src="/logo.png" alt={appTitle} className="w-6 h-6 object-contain" />
      </Link>
      <div className="w-10 h-px bg-white/20 my-3" />
      <div
        className="flex flex-col items-center gap-2 w-full"
        role="tablist"
        aria-label="App mode tabs"
      >
        {topItems.map((item) => {
          // Visually group `ads` separately from the scan-based modes
          // (lightning / video / interior) with a thin divider below it.
          const showSpacerAfter = item.modeId === 'ads';
          return (
            <Fragment key={item.modeId}>
              {renderRailLink(item)}
              {showSpacerAfter && (
                <div className="w-10 h-px bg-white/20 my-1" aria-hidden="true" />
              )}
            </Fragment>
          );
        })}
      </div>
      {/* Docs-only Release Notes entry pinned to the very bottom of the rail. */}
      {bottomItems.length > 0 && (
        <div className="mt-auto flex flex-col items-center gap-2 w-full pt-2">
          <div className="w-10 h-px bg-white/20 my-1" aria-hidden="true" />
          {bottomItems.map((item) => (
            <Fragment key={item.modeId}>{renderRailLink(item)}</Fragment>
          ))}
        </div>
      )}
    </aside>
  );
}

function visibleModeRailItems(
  videoVisible: boolean,
  interiorVisible: boolean,
  adsVisible: boolean,
): ModeRailItem[] {
  return modeRailItems.filter((item) => {
    if (item.modeId === 'video') return videoVisible;
    if (item.modeId === 'interior') return interiorVisible;
    if (item.modeId === 'ads') return adsVisible;
    return true;
  });
}

interface StatusFooterProps {
  beLive: boolean;
  showDbIndicator: boolean;
  dbLive: boolean;
  authBypassActive: boolean;
  beVersion?: string;
  localBuild: boolean;
  dummyMode: boolean;
  centered?: boolean;
}

/**
 * Shared BE/DB liveness + git/version footer. Rendered in the desktop
 * sidebar and inside the mobile drawer so both surfaces stay in sync.
 */
function StatusFooter({
  beLive,
  showDbIndicator,
  dbLive,
  authBypassActive,
  beVersion,
  localBuild,
  dummyMode,
  centered = false,
}: StatusFooterProps) {
  return (
    <div className={`px-4 py-4 border-t border-slate-200 text-[11px] text-slate-500 space-y-1.5 ${centered ? 'flex flex-col items-center text-center border-t-0' : ''}`}>
      <div className={`flex items-center gap-2 flex-wrap font-mono text-slate-400 ${centered ? 'justify-center' : ''}`}>
        {__GIT_BRANCH__ !== 'unknown' && (
          <a
            href={`${GITHUB_REPO_URL}/tree/${__GIT_BRANCH__}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate max-w-[8rem] hover:text-indigo-600"
            title={`Branch: ${__GIT_BRANCH__}`}
          >
            {__GIT_BRANCH__}
          </a>
        )}
        {__GIT_BRANCH__ !== 'unknown' && __GIT_COMMIT__ !== 'unknown' && (
          <span className="text-slate-300">@</span>
        )}
        {__GIT_COMMIT__ !== 'unknown' && (
          <a
            href={`${GITHUB_REPO_URL}/commit/${__GIT_COMMIT__}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-indigo-600"
            title={`Commit: ${__GIT_COMMIT__}`}
          >
            {__GIT_COMMIT__.slice(0, 7)}
          </a>
        )}
        {__GIT_BRANCH__ === 'unknown' && __GIT_COMMIT__ === 'unknown' && (
          <span className="italic text-slate-400">git info unavailable</span>
        )}
        {localBuild && (
          <span
            className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-amber-50 text-amber-600"
            title="Running a local `make deploy` build, not a CI deployment"
          >
            LOCAL
          </span>
        )}
        {dummyMode && (
          <span
            className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-purple-50 text-purple-600"
            title="Dummy mode is ON: paid generative calls (OpenAI / FAL / Gemini) are replaced with sleeping no-ops that return placeholder results"
          >
            DUMMY
          </span>
        )}
      </div>
      <div className={`flex items-center gap-3 ${centered ? 'justify-center' : ''}`}>
        <span
          className="inline-flex items-center gap-1"
          title={beLive ? 'Backend service is responding' : 'Backend service is not reachable'}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              beLive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            }`}
          />
          <span className={beLive ? 'text-green-600' : 'text-red-600'}>
            BE {beLive ? 'online' : 'offline'}
          </span>
        </span>
        {showDbIndicator && (
          <span
            className="inline-flex items-center gap-1"
            title={dbLive ? 'Database is reachable' : 'Database is not reachable'}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                dbLive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
              }`}
            />
            <span className={dbLive ? 'text-green-600' : 'text-red-600'}>
              DB {dbLive ? 'online' : 'offline'}
            </span>
          </span>
        )}
      </div>
      {authBypassActive && (
        <div className={centered ? 'flex justify-center' : ''}>
          <span
            className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-medium"
            title="Set VITE_SKIP_AUTH=false to require Google sign-in"
          >
            Dev bypass
          </span>
        </div>
      )}
      <div className="font-mono text-slate-400">v{__APP_VERSION__}</div>
      {beVersion && (
        <div className="font-mono text-slate-400">be v{beVersion}</div>
      )}
    </div>
  );
}

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  mode: AppMode;
  navItems: NavItem[];
  generateLink: string | null;
  generateLabel: string;
  showVideoGenerate: boolean;
  onVideoGenerate: () => void;
  expandedNavGroups: ReadonlySet<string>;
  onToggleNavGroup: (groupId: string) => void;
  footer?: ReactNode;
}

/**
 * Off-canvas navigation for phones (`< md`). Holds everything the
 * desktop rail + sidebar provide: the mini-app switcher, the active
 * mode's section links, the Generate action and the status footer.
 */
function MobileDrawer({
  open,
  onClose,
  mode,
  navItems,
  generateLink,
  generateLabel,
  showVideoGenerate,
  onVideoGenerate,
  expandedNavGroups,
  onToggleNavGroup,
  footer,
}: MobileDrawerProps) {
  const { isAuthenticated, authBypassActive } = useAuth();
  const showLogout = isAuthenticated && !authBypassActive;
  return (
    <div
      className={`md:hidden fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        className={`absolute inset-y-0 left-0 w-64 max-w-[85vw] bg-white shadow-xl flex flex-col transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Navigation"
      >
        <div className="flex items-center justify-between px-4 h-14 border-b border-slate-200 bg-indigo-500 text-white shrink-0">
          <span className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="w-6 h-6 object-contain" />
            <span className="font-semibold text-sm">{appTitleFor(mode)}</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg hover:bg-white/15 transition-colors"
            aria-label="Close navigation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pt-2">
          {/* Generate action */}
          {generateLink && (
            <div className="px-3 pb-2">
              <Link
                to={generateLink}
                className="flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md"
              >
                {generateLabel}
              </Link>
            </div>
          )}
          {showVideoGenerate && (
            <div className="px-3 pb-2">
              <button
                type="button"
                onClick={onVideoGenerate}
                className="w-full flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md"
              >
                Generate Full360
              </button>
            </div>
          )}

          {/* Section nav */}
          <nav className="px-3 pt-1 pb-4 space-y-1">
            {navItems.map((item) => (
              <Fragment key={item.to}>
                {item.groupLabel && (
                  <NavGroupButton
                    label={item.groupLabel}
                    expanded={!item.groupId || expandedNavGroups.has(item.groupId)}
                    onToggle={() => {
                      if (item.groupId) onToggleNavGroup(item.groupId);
                    }}
                  />
                )}
                {item.groupId && !expandedNavGroups.has(item.groupId) ? null : item.disabledReason ? (
                  <div
                    title={item.disabledReason}
                    aria-disabled="true"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 cursor-not-allowed select-none ${
                      item.nested ? 'ml-4 border-l border-slate-200' : ''
                    }`}
                  >
                    <span className="text-slate-300">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ) : (
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        item.nested ? 'ml-4 border-l border-slate-200' : ''
                      } ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-600 font-medium'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? 'text-indigo-600' : 'text-slate-400'}>
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                )}
              </Fragment>
            ))}
          </nav>
        </div>

        {/* Logout sits at the bottom of the drawer on mobile, replacing the
            top-bar Login button so the header stays uncluttered. */}
        {showLogout && (
          <div className="px-3 py-3 border-t border-slate-200 shrink-0 flex [&>button]:w-full [&>button]:justify-center">
            <Login />
          </div>
        )}

        {footer}
      </aside>
    </div>
  );
}

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { health: lightningHealth, beLive: lightningBeLive, dbLive: lightningDbLive } =
    useHealth();
  // The video health hook is always mounted (even in lightning mode) so
  // we can show its counts immediately on a tab switch without a 5s
  // delay. When the video service isn't enabled at build time, video
  // requests still go out but get a 4xx/network error and the hook just
  // reports beLive=false, which is harmless because we only consume it
  // while `mode === 'video'`.
  const { health: videoHealth, beLive: videoBeLive } = useVideoHealth();
  // Interior health hook is always mounted for the same reason as the
  // video one above — so tab switches don't introduce a 5s blank state
  // before counts populate. Costs nothing in lightning mode beyond a
  // background poll.
  const { health: interiorHealth, beLive: interiorBeLive } = useInteriorHealth();
  // Ads health hook is always mounted for the same tab-switch reason
  // as the video/interior hooks above.
  const { health: adsHealth, beLive: adsBeLive } = useAdsHealth();
  // Gen API (apigen) runs as its own process rather than being mounted inside
  // lightning, so it needs its own poll — the lightning `/health` says nothing
  // about whether apigen is reachable.
  const { health: apigenHealth, beLive: apigenBeLive } = useApigenHealth();
  const { authBypassActive } = useAuth();
  const location = useLocation();
  const mode = detectMode(location.pathname);
  const videoVisible = isVideoEnabled();
  const interiorVisible = isInteriorEnabled();
  const adsVisible = isAdsEnabled();
  // Manual review is a per-request flag now, so the Manual Review tab is
  // always available for the interior app.
  const interiorNav = interiorNavItems;
  const navItems =
    mode === 'video'
      ? videoNavItems
      : mode === 'interior'
        ? interiorNav
        : mode === 'ads'
          ? adsNavItems
          : mode === 'api'
            ? apiNavItems
            : mode === 'cost'
              ? costNavItems
              : mode === 'release-notes'
                ? releaseNotesNavItems
                : lightningNavItems;
  const homeLink =
    mode === 'video'
      ? '/video/overview'
      : mode === 'interior'
        ? '/interior/overview'
        : mode === 'ads'
          ? '/ads/overview'
          : mode === 'api'
            ? '/gen-api'
            : mode === 'cost'
              ? '/cost/services'
              : mode === 'release-notes'
                ? `/release-notes/${latestReleaseVersion}`
                : '/lightning/overview';
  // In lightning + interior + ads modes the "Generate" button is a
  // route link to a full page. In video mode it opens an inline modal.
  const generateLink =
    mode === 'video' || mode === 'release-notes'
      ? null
      : mode === 'interior'
        ? '/interior/generate'
        : mode === 'ads'
          ? '/ads/generate'
          : mode === 'api'
            ? null
            : mode === 'cost'
              ? null
              : '/lightning/generate';
  const generateLabel =
    mode === 'interior'
      ? 'Generate Interior'
      : mode === 'ads'
        ? 'Generate Ad'
        : 'Generate Merch';
  const activeNavGroup = releaseNotesNavItems.find(
    (item) => item.groupId && item.to === location.pathname,
  )?.groupId;
  const [expandedNavGroups, setExpandedNavGroups] = useState<Set<string>>(
    () => new Set(activeNavGroup ? [activeNavGroup] : []),
  );
  const [videoGenerateOpen, setVideoGenerateOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileAppSwitcherOpen, setMobileAppSwitcherOpen] = useState(false);

  // Keep the group containing the selected patch visible after navigation.
  useEffect(() => {
    if (!activeNavGroup) return;
    setExpandedNavGroups((current) => {
      if (current.has(activeNavGroup)) return current;
      const next = new Set(current);
      next.add(activeNavGroup);
      return next;
    });
  }, [activeNavGroup]);

  const toggleNavGroup = (groupId: string) => {
    setExpandedNavGroups((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Close the mobile drawer whenever the route changes so tapping a link
  // navigates and dismisses the overlay in one gesture.
  useEffect(() => {
    setMobileNavOpen(false);
    setMobileAppSwitcherOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open so the page behind it
  // doesn't scroll under the overlay.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileNavOpen]);

  const mobileModeItems = visibleModeRailItems(videoVisible, interiorVisible, adsVisible);

  // Header pills follow the active mini-app: lightning's flat counts vs
  // video/interior's nested `counts` object. Each backend reports only
  // its own jobs, so switching tabs swaps the numbers.
  const counts: SystemStatusCounts | null =
    mode === 'release-notes'
      ? null
      : mode === 'video'
      ? videoHealth
        ? {
            active: videoHealth.counts.processing,
            pending: videoHealth.counts.pending,
            completed: videoHealth.counts.completed,
            failed: videoHealth.counts.failed,
          }
        : null
      : mode === 'interior'
        ? interiorHealth
          ? {
              active: interiorHealth.counts.processing,
              pending: interiorHealth.counts.pending,
              completed: interiorHealth.counts.completed,
              failed: interiorHealth.counts.failed,
            }
          : null
        : mode === 'ads'
          ? adsHealth
            ? {
                active: adsHealth.counts.processing,
                pending: adsHealth.counts.pending,
                completed: adsHealth.counts.completed,
                failed: adsHealth.counts.failed,
              }
            : null
          : mode === 'api' || mode === 'cost'
            ? null
            : lightningHealth
          ? {
              active: lightningHealth.active_jobs,
              pending: lightningHealth.pending_jobs,
              completed: lightningHealth.completed_jobs,
              failed: lightningHealth.failed_jobs,
            }
          : null;

  const beLive =
    mode === 'video'
      ? videoBeLive
      : mode === 'interior'
        ? interiorBeLive
        : mode === 'ads'
          ? adsBeLive
          : mode === 'api'
            ? apigenBeLive
            : lightningBeLive;
  // The video + interior services don't currently expose a separate
  // DB-liveness signal on `/health`, so in those modes we only show
  // the BE indicator.
  const showDbIndicator = mode === 'lightning';
  const dbLive = lightningDbLive;
  const beVersion =
    mode === 'video'
      ? videoHealth?.version
      : mode === 'interior'
        ? interiorHealth?.version
        : mode === 'ads'
          ? adsHealth?.version
          : mode === 'api'
            ? apigenHealth?.version
            : lightningHealth?.version;
  const localBuild = mode === 'lightning' ? Boolean(lightningHealth?.local_build) : false;
  const dummyMode = mode === 'lightning' ? Boolean(lightningHealth?.dummy_mode) : false;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Mode rail */}
      <ModeRail
        mode={mode}
        videoVisible={videoVisible}
        interiorVisible={interiorVisible}
        adsVisible={adsVisible}
        homeLink={homeLink}
      />

      {/* Sidebar */}
      <aside className="hidden md:flex w-52 shrink-0 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen self-start z-30">
        <nav className="px-3 pt-4 flex-1 overflow-y-auto">
          {generateLink && (
            <>
              <div className="flex justify-center">
                <Link
                  to={generateLink}
                  className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md hover:shadow-lg hover:opacity-95 transition-all duration-200"
                >
                  {generateLabel}
                </Link>
              </div>
              <div className="my-3 border-t border-slate-200" />
            </>
          )}
          {mode === 'video' && (
            <>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setVideoGenerateOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md hover:shadow-lg hover:opacity-95 transition-all duration-200"
                >
                  Generate Full360
                </button>
              </div>
              <div className="my-3 border-t border-slate-200" />
            </>
          )}
          <div className="space-y-1">
            {navItems.map((item) => (
              <Fragment key={item.to}>
                {item.groupLabel && (
                  <NavGroupButton
                    label={item.groupLabel}
                    expanded={!item.groupId || expandedNavGroups.has(item.groupId)}
                    onToggle={() => {
                      if (item.groupId) toggleNavGroup(item.groupId);
                    }}
                  />
                )}
                {item.groupId && !expandedNavGroups.has(item.groupId) ? null : item.disabledReason ? (
                  <div
                    title={item.disabledReason}
                    aria-disabled="true"
                    className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 cursor-not-allowed select-none ${
                      item.nested ? 'ml-4 border-l border-slate-200' : ''
                    }`}
                  >
                    <span className="text-slate-300">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ) : (
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        item.nested ? 'ml-4 border-l border-slate-200' : ''
                      } ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-600 font-medium'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
                        )}
                        <span className={isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}>
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                )}
              </Fragment>
            ))}
          </div>
        </nav>

        <StatusFooter
          beLive={beLive}
          showDbIndicator={showDbIndicator}
          dbLive={dbLive}
          authBypassActive={authBypassActive}
          beVersion={beVersion}
          localBuild={localBuild}
          dummyMode={dummyMode}
        />
      </aside>

      {/* Mobile drawer (off-canvas nav) */}
      <MobileDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        mode={mode}
        navItems={navItems}
        generateLink={generateLink}
        generateLabel={generateLabel}
        showVideoGenerate={mode === 'video'}
        onVideoGenerate={() => {
          setMobileNavOpen(false);
          setVideoGenerateOpen(true);
        }}
        expandedNavGroups={expandedNavGroups}
        onToggleNavGroup={toggleNavGroup}
        footer={null}
      />

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden relative sticky top-0 z-30 flex items-center gap-2 h-14 px-3 border-b border-indigo-600 bg-indigo-500 text-white">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="p-2 -ml-1 rounded-lg hover:bg-white/15 transition-colors"
            aria-label="Open navigation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 font-semibold text-sm max-w-[60%] pointer-events-none">
            <img src="/logo.png" alt="" className="w-6 h-6 object-contain shrink-0" />
            <span className="truncate">{appTitleFor(mode)}</span>
          </span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative">
              <button
                type="button"
                onClick={() => setMobileAppSwitcherOpen(!mobileAppSwitcherOpen)}
                className="p-1 rounded-lg hover:bg-white/15 transition-colors"
                aria-label="Change application view"
                title="Change application view"
              >
                <AppLogo 
                  src={mobileModeItems.find(item => item.modeId === mode)?.iconSrc || '/lightning.png'} 
                  alt={appTitleFor(mode)} 
                  className="w-7 h-7" 
                  colorClass="bg-white"
                />
              </button>
              
              {mobileAppSwitcherOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setMobileAppSwitcherOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 overflow-hidden">
                    {mobileModeItems.map((item) => {
                      const isActive = item.modeId === mode;
                      return (
                        <Link
                          key={item.modeId}
                          to={item.to}
                          className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                            isActive
                              ? 'bg-indigo-50 text-indigo-600 font-medium'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <AppLogo src={item.iconSrc} alt="" className="w-6 h-6" colorClass="bg-indigo-600" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm md:sticky md:top-0 z-20">
          <div className="px-4 sm:px-6 py-2 flex items-center gap-4 flex-wrap">
            <div className="flex-1 flex justify-start min-w-0">
              <SystemStatusHeader counts={counts} mode={mode} />
            </div>

            <div className="flex items-center gap-4 shrink-0 justify-end">
              <div className="hidden lg:block">
                <Login />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-4 sm:px-6 sm:py-6">
          <div className={location.pathname.endsWith('/overview') ? 'hidden md:block' : ''}>
            {children}
          </div>
          {location.pathname.endsWith('/overview') && (
            <div className="md:hidden flex flex-col justify-center items-center h-full min-h-[50vh]">
              <StatusFooter
                beLive={beLive}
                showDbIndicator={showDbIndicator}
                dbLive={dbLive}
                authBypassActive={authBypassActive}
                beVersion={beVersion}
                localBuild={localBuild}
                dummyMode={dummyMode}
                centered={true}
              />
            </div>
          )}
        </main>
      </div>

      {/* Video-only "Generate Scan" modal lives at the shell level so it
          can be triggered from the header on any /video/* page. */}
      {mode === 'video' && (
        <GenerateScanModal
          open={videoGenerateOpen}
          onClose={() => setVideoGenerateOpen(false)}
          onSubmitted={() => {
            window.dispatchEvent(new CustomEvent(VIDEO_SCAN_SUBMITTED_EVENT));
          }}
        />
      )}
    </div>
  );
}
